import { createElement, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl-next';
import { useLoomMeetingNudgeAnalytics } from '@post-office/message-template--twc-loom-meeting-nudge-sidenav/analytics';

import CalendarIcon from '@atlaskit/icon/core/calendar';
import { token } from '@atlaskit/tokens';

import { openLoomMeetingRecordings } from '@atlassian/loom-meeting-recordings';

import { fg } from '@confluence/feature-gating';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState/onboardingState.ts';
import { LOOM_NUDGE_REMIND_ME } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	clearLocalMeetingSignalReminderPending,
	useIsLocalMeetingSignalReminderPending,
} from './useIsLocalMeetingSignalReminderPending.ts';

type MeetingSignalReminderParams =
	| { flags?: FlagsStateContainer; consumptionBoundary: 'pageLoad'; contentId: string }
	| { flags?: FlagsStateContainer; consumptionBoundary: 'publish'; contentId?: never };

type MeetingSignalReminderResult = {
	consumeReminder: () => void;
	isEnabled: boolean;
	isPending: boolean;
};

/**
 * Reads and consumes a deferred Loom meeting-nudge reminder.
 *
 * The same persisted reminder can be consumed at either boundary. The publish boundary does not
 * need a content ID. The page-load boundary requires `contentId` to distinguish client-side
 * transitions and waits for the next load, whether the user navigates to different content or
 * reloads the current page.
 */
export const useMeetingSignalReminder = ({
	consumptionBoundary,
	contentId,
	flags,
}: MeetingSignalReminderParams): MeetingSignalReminderResult => {
	const intl = useIntl();
	const { cloudId } = useSessionData();
	const { fireCtaClicked, fireScreenSeen, fireFlagDismissed } =
		useLoomMeetingNudgeAnalytics('sidenav');
	const { setOnboardingState } = useSetOnboardingState();
	const isLocalReminderPending = useIsLocalMeetingSignalReminderPending();

	const isReminderEnabled =
		fg('twcg-2202-loom-meeting-nudge-ht-gate') || fg('twcg-2202-loom-meeting-nudge-lt-gate');

	const { data, loading } = useGetOnboardingState(LOOM_NUDGE_REMIND_ME, !isReminderEnabled);
	const reminderState = deserializeState(data) as Readonly<Record<string, unknown>>;
	const isPendingFromStateOrRequest =
		isReminderEnabled && (reminderState.remindMe === true || isLocalReminderPending);

	// A page-load reminder only qualifies if it was already pending when this content
	// loaded. Later cache updates for the same content must not change the snapshot.
	const pageLoadSnapshotRef = useRef({
		contentId,
		isResolved: false,
		isPending: false,
	});

	// Effects can run more than once, so track consumption independently of the
	// persisted onboarding state while the current content remains mounted.
	const hasConsumedRef = useRef({ contentId, hasConsumed: false });

	// SPA navigation can replace the content without remounting this hook. A new
	// content ID creates a fresh load boundary and may consume a deferred reminder.
	if (pageLoadSnapshotRef.current.contentId !== contentId) {
		pageLoadSnapshotRef.current = { contentId, isResolved: false, isPending: false };
	}

	// Capture only the first resolved onboarding-state read for this content load.
	if (!pageLoadSnapshotRef.current.isResolved && !loading && data !== undefined) {
		pageLoadSnapshotRef.current = {
			contentId,
			isResolved: true,
			isPending: isPendingFromStateOrRequest,
		};
	}

	// Reset the exactly-once guard alongside the page-load content boundary.
	if (hasConsumedRef.current.contentId !== contentId) {
		hasConsumedRef.current = { contentId, hasConsumed: false };
	}

	// Publishes use current state; page-load consumers use their load-time snapshot.
	const isPending =
		consumptionBoundary === 'pageLoad'
			? pageLoadSnapshotRef.current.isPending
			: isPendingFromStateOrRequest;

	const consumeReminder = useCallback((): void => {
		// Missing UI infrastructure, no reminder intent, or a repeated effect are all
		// no-ops. In particular, do not clear the intent when flags are unavailable.
		if (!flags || !isPending || hasConsumedRef.current.hasConsumed) {
			return;
		}

		// Guard before either side effect so concurrent consumers cannot show twice.
		hasConsumedRef.current.hasConsumed = true;
		clearLocalMeetingSignalReminderPending();

		// Clear persistence as the reminder is handed to the flag system. The ref still
		// prevents duplicates while the onboarding-state mutation propagates.
		setOnboardingState({ key: LOOM_NUDGE_REMIND_ME, value: 'false' });
		const flagId = `loom-meeting-nudge-connect-calendar-${consumptionBoundary}`;
		void flags.showFlag({
			id: flagId,
			type: 'custom',
			// Publishing unmounts EditorFlags, which calls hideAllFlags(). Keeping this
			// flag lets it wait behind the publish-success flag until manual dismissal.
			close: 'never',
			onShown: () => fireScreenSeen('dontForgetToConnectCalendar'),
			onClose: () => fireFlagDismissed('flagConnectCal', 'close'),
			customIcon: createElement(CalendarIcon, {
				label: '',
				color: token('color.icon'),
				spacing: 'spacious',
			}),
			title: intl.formatMessage({
				id: 'experiment-meeting-signal.connect-calendar-reminder.title.ai-non-final',
				defaultMessage: 'Don’t forget to connect your calendar',
				description: 'Title reminding the user to connect their calendar to Loom.',
			}),
			description: intl.formatMessage({
				id: 'experiment-meeting-signal.connect-calendar-reminder.description.ai-non-final',
				defaultMessage:
					'Loom can capture action items and notes directly in Confluence and Jira so you don’t have to.',
				description: 'Description of the benefit of connecting a calendar to Loom.',
			}),
			actions: [
				{
					content: intl.formatMessage({
						id: 'experiment-meeting-signal.connect-calendar-reminder.action.ai-non-final',
						defaultMessage: 'Connect calendar',
						description: 'Action to open Loom calendar setup from a reminder flag.',
					}),
					onClick: () => {
						fireCtaClicked('dontForgetToConnectCalendar');
						void flags.hideFlag(flagId);
						openLoomMeetingRecordings({
							bridge: 'loom-meeting-nudge-flag',
							cloudId,
							interactionSource: 'confluence',
							loomSource: 'confluence-nudge-flag',
						});
					},
				},
			],
		});
	}, [
		cloudId,
		consumptionBoundary,
		fireCtaClicked,
		fireFlagDismissed,
		fireScreenSeen,
		flags,
		intl,
		isPending,
		setOnboardingState,
	]);

	return { consumeReminder, isEnabled: isReminderEnabled, isPending };
};
